import React from "react";
import styled, { css } from "styled-components";
import TimePicker from "react-time-picker";

const Group = styled.div`
  display: flex;
  flex-direction: column;
  gap: var(--space-2);
`;

const DayRow = styled.div`
  display: flex;
  align-items: center;
  gap: var(--space-3);
`;

const DayToggle = styled.button<{ $active: boolean }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  padding: 0;
  background: var(--color-surface);
  border: 1px solid var(--color-border);
  border-radius: var(--radius-md);
  font-family: var(--font-sans);
  font-size: var(--font-size-xs);
  font-weight: var(--font-weight-semibold);
  color: var(--color-text-secondary);
  cursor: pointer;
  flex-shrink: 0;
  transition:
    background var(--duration-fast) var(--ease-standard),
    border-color var(--duration-fast) var(--ease-standard),
    color var(--duration-fast) var(--ease-standard);

  &:hover {
    background: var(--color-surface-subtle);
    border-color: var(--color-border-strong);
    color: var(--color-text);
  }

  &:focus-visible {
    outline: none;
    box-shadow: var(--shadow-focus);
  }

  ${(p) =>
    p.$active &&
    css`
      background: var(--color-accent);
      color: var(--color-text-inverse);
      border-color: var(--color-accent);

      &:hover {
        background: var(--color-accent-hover);
        border-color: var(--color-accent-hover);
        color: var(--color-text-inverse);
      }
    `}
`;

const TimePickerWrapper = styled.div`
  flex: 1;
  min-width: 0;

  .react-time-picker__wrapper {
    background: var(--color-surface);
    border: 1px solid var(--color-border);
    border-radius: var(--radius-md);
    padding: 4px 8px;
    color: var(--color-text);
    font-family: var(--font-sans);
    font-size: var(--font-size-sm);
    transition:
      border-color var(--duration-fast) var(--ease-standard),
      box-shadow var(--duration-fast) var(--ease-standard);
  }

  .react-time-picker__wrapper:focus-within {
    border-color: var(--color-accent);
    box-shadow: var(--shadow-focus);
  }

  .react-time-picker__inputGroup__input,
  .react-time-picker__inputGroup__divider,
  .react-time-picker__inputGroup__leadingZero,
  .react-time-picker__inputGroup__amPm {
    color: var(--color-text) !important;
    font-family: var(--font-sans) !important;
  }

  .react-time-picker__button {
    color: var(--color-text-secondary);
  }

  .react-time-picker__clock {
    background: var(--color-surface);
    border: 1px solid var(--color-border);
    border-radius: var(--radius-lg);
    box-shadow: var(--shadow-md);
  }
`;

type Day = {
  dayOfWeek: string;
  isActive: boolean;
  time: string;
};

type Props = {
  days: Day[];
  setDays: (index: number) => void;
  setTime: (index: number, date: any) => void;
};

const DaysOfWeek = ({ days, setDays, setTime }: Props) => {
  return (
    <Group>
      {days.map((day, index) => (
        <DayRow key={day.dayOfWeek}>
          <DayToggle
            type="button"
            $active={day.isActive}
            aria-pressed={day.isActive}
            aria-label={day.dayOfWeek}
            onClick={() => setDays(index)}
          >
            {day.dayOfWeek}
          </DayToggle>
          <TimePickerWrapper>
            <TimePicker
              name="time"
              value={day.time}
              onChange={(date) => setTime(index, date)}
            />
          </TimePickerWrapper>
        </DayRow>
      ))}
    </Group>
  );
};

export default DaysOfWeek;
